import { useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';

import {
  SimpleGrid,
  Box,
  Text,
  Alert,
  AlertIcon,
  Button,
  Icon,
  Flex,
  Collapse,
  useColorModeValue,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import FormInput from 'components/fields/auto/FormInput';

import { SECTIONS } from 'views/auto_form/constants';

import { APIPersonalAutoApplicationFormVehicle } from 'types/api.types';

import {
  changeVehicle,
  changeVehicleLienholder,
} from 'reducers/autoPersonalFormReducer';
import {
  isNonOntarioProvince,
  PROVINCE_NEW_BRUNSWICK,
} from 'variables/constants';
import isSameAsMailingAddress from 'views/auto_form/sections/Vehicles/helpers/isSameAsMailingAddress';

const LienHolder = ({
  vehicle,
  index,
}: {
  vehicle: APIPersonalAutoApplicationFormVehicle;
  index: number;
}) => {
  const dispatch = useAppDispatch();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const applicant = useAppSelector(
    (state) => state.autoPersonalForm.data.applicant,
  );
  const isNonOntario = isNonOntarioProvince(province);
  const isDisabled =
    province === PROVINCE_NEW_BRUNSWICK && Boolean(vehicle.is_leased);

  const isMandatory = vehicle._info.is_lienholder_mandatory;
  const lienholderInfo = vehicle.lienholder_info;

  const hasValues = isNonOntario
    ? Boolean(
        lienholderInfo?.name ||
        lienholderInfo?.address ||
        lienholderInfo?.city ||
        lienholderInfo?.province_code ||
        lienholderInfo?.postal_code,
      )
    : Boolean(vehicle.lienholder);

  const [isLienHolderCollapsed, setIsLienHolderCollapsed] = useState(
    !(isMandatory || hasValues),
  );

  const onVehicleChange = (event: any) => {
    const { name, value } = event.target;
    dispatch(changeVehicle({ name, index, value }));
  };

  const onLienholderChange = (event: any) => {
    const { name, value } = event.target;
    dispatch(changeVehicleLienholder({ index, name, value }));
  };

  const isSameAsMailing =
    isNonOntario && isSameAsMailingAddress(lienholderInfo, applicant);

  return (
    <Box p="20px" py="10px" borderTop="1px solid" borderColor={borderColor}>
      <Flex justifyContent="space-between" alignItems="center">
        <Box fontWeight="bold" fontSize="l">
          Lienholder Information
        </Box>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsLienHolderCollapsed(!isLienHolderCollapsed)}
          rightIcon={
            <Icon
              as={isLienHolderCollapsed ? ChevronDownIcon : ChevronUpIcon}
            />
          }
        >
          {isLienHolderCollapsed
            ? 'Show Lienholder Information'
            : 'Hide Lienholder Information'}
        </Button>
      </Flex>
      <Collapse in={!isLienHolderCollapsed} animateOpacity>
        <Box my="10px">
          {isNonOntario ? (
            <>
              <SimpleGrid columns={5} spacing="20px">
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Name"
                  subLabel={`Vehicle ${index + 1} Lienholder`}
                  name="name"
                  value={lienholderInfo?.name || ''}
                  onChange={onLienholderChange}
                  required={isMandatory}
                  disabled={isDisabled}
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Address"
                  subLabel={`Vehicle ${index + 1} Lienholder`}
                  name="address"
                  value={lienholderInfo?.address || ''}
                  onChange={onLienholderChange}
                  required={isMandatory}
                  disabled={isDisabled}
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="City"
                  subLabel={`Vehicle ${index + 1} Lienholder`}
                  name="city"
                  value={lienholderInfo?.city || ''}
                  onChange={onLienholderChange}
                  required={isMandatory}
                  disabled={isDisabled}
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Province code"
                  subLabel={`Vehicle ${index + 1} Lienholder`}
                  name="province_code"
                  value={lienholderInfo?.province_code || ''}
                  onChange={onLienholderChange}
                  required={isMandatory}
                  disabled={isDisabled}
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Postal code"
                  subLabel={`Vehicle ${index + 1} Lienholder`}
                  name="postal_code"
                  value={lienholderInfo?.postal_code || ''}
                  onChange={onLienholderChange}
                  required={isMandatory}
                  disabled={isDisabled}
                />
              </SimpleGrid>

              {isSameAsMailing && (
                <Text color="red.600" fontSize="sm" mt="10px">
                  Lienholder address must not be the same as the mailing address
                </Text>
              )}
            </>
          ) : (
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Lienholder Name & Postal Address"
              name="lienholder"
              value={vehicle.lienholder}
              onChange={onVehicleChange}
              required={isMandatory}
              subLabel={`Vehicle ${index + 1}`}
            />
          )}

          {isMandatory && (
            <Alert status="warning" fontSize="12px" mt="10px">
              <AlertIcon />
              <Box>
                "#23a Mortgage" coverage detected, lienholder information is
                mandatory.
              </Box>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default LienHolder;
